/* AnnotationLayer — transparent draw-over layer for live shared overlays
 * (story / slides / chapter / resource). The teacher gets a pen toolbar and
 * every committed stroke syncs to students through the overlay payload
 * (payload.annotations) via /live/{id}/overlay-update. Students render-only. */
import React, { useEffect, useRef, useState } from "react";
import { Pen, Highlighter, Eraser, Undo2, Trash2, X } from "lucide-react";

const COLORS = ["#EF4444", "#22D3EE", "#FFD600", "#10B981", "#FFFFFF"];
const SIZES = [2.5, 4.5, 7];

export function AnnotationLayer({ strokes = [], editable = false, onCommit, zBase = 20 }) {
  const canvasRef = useRef(null);
  const [active, setActive] = useState(false);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#EF4444");
  const [size, setSize] = useState(4.5);
  const drawingRef = useRef(null);
  const strokesRef = useRef(strokes);
  useEffect(() => { strokesRef.current = strokes; redraw(); /* eslint-disable-next-line */ }, [strokes]);

  const redraw = () => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    const paint = (s) => {
      if (!s?.pts?.length) return;
      ctx.beginPath();
      ctx.lineJoin = "round"; ctx.lineCap = "round";
      ctx.strokeStyle = s.color || "#EF4444";
      ctx.globalAlpha = s.tool === "hl" ? 0.35 : 1;
      ctx.lineWidth = Math.max(1.5, (s.tool === "hl" ? (s.size || 4.5) * 3.5 : (s.size || 4.5)) * (cv.width / 1400));
      s.pts.forEach(([x, y], i) => { const px = x * cv.width, py = y * cv.height; i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
      ctx.stroke();
      ctx.globalAlpha = 1;
    };
    strokesRef.current.forEach(paint);
    if (drawingRef.current && !drawingRef.current.erasing) paint(drawingRef.current);
  };

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const fit = () => { cv.width = cv.offsetWidth; cv.height = cv.offsetHeight; redraw(); };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const norm = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };
  const erase = (p) => {
    const keep = strokesRef.current.filter((s) => !(s.pts || []).some(([x, y]) => Math.abs(x - p[0]) < 0.02 && Math.abs(y - p[1]) < 0.035));
    if (keep.length !== strokesRef.current.length) { strokesRef.current = keep; onCommit?.(keep); redraw(); }
  };
  const down = (e) => {
    if (!active) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const p = norm(e);
    if (tool === "eraser") { erase(p); drawingRef.current = { erasing: true }; return; }
    drawingRef.current = { tool, color, size, pts: [p] };
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    const p = norm(e);
    if (drawingRef.current.erasing) { erase(p); return; }
    const pts = drawingRef.current.pts;
    const last = pts[pts.length - 1];
    if (Math.abs(p[0] - last[0]) + Math.abs(p[1] - last[1]) > 0.0025) { pts.push(p); redraw(); }
  };
  const up = () => {
    const d = drawingRef.current;
    drawingRef.current = null;
    if (!d || d.erasing || (d.pts || []).length < 2) { redraw(); return; }
    strokesRef.current = [...strokesRef.current, d];
    onCommit?.(strokesRef.current);
    redraw();
  };
  const undo = () => { strokesRef.current = strokesRef.current.slice(0, -1); onCommit?.(strokesRef.current); redraw(); };
  const clearAll = () => { strokesRef.current = []; onCommit?.([]); redraw(); };

  const tb = (on) => `w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors ${on ? "bg-[#22D3EE] text-[#0A0C10]" : "text-white/70 hover:bg-white/10"}`;
  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="annotation-canvas"
        className="absolute inset-0 w-full h-full touch-none"
        style={{ zIndex: zBase, pointerEvents: active ? "auto" : "none", cursor: active ? (tool === "eraser" ? "cell" : "crosshair") : "default" }}        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      />
      {editable && !active && (
        <button
          onClick={() => setActive(true)}
          data-testid="annotate-toggle-btn"
          title="Annotate — draw on top of what you're presenting"
          className="absolute top-16 right-4 z-30 pointer-events-auto inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#22D3EE] text-white text-xs font-bold shadow-[0_0_20px_rgba(34,211,238,0.5)] hover:scale-105 transition-transform"
        ><Pen size={14} /> Annotate</button>
      )}
      {editable && active && (
        <div
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#0D1117]/95 border border-white/15 shadow-2xl backdrop-blur"
          data-testid="annotation-toolbar"
        >
          <button className={tb(tool === "pen")} onClick={() => setTool("pen")} data-testid="anno-pen" title="Pen"><Pen size={14} /></button>
          <button className={tb(tool === "hl")} onClick={() => setTool("hl")} data-testid="anno-highlighter" title="Highlighter"><Highlighter size={14} /></button>
          <button className={tb(tool === "eraser")} onClick={() => setTool("eraser")} data-testid="anno-eraser" title="Eraser"><Eraser size={14} /></button>
          <span className="w-px h-5 bg-white/15 mx-0.5" />
          {COLORS.map((c) => (
            <button key={c} onClick={() => { setColor(c); if (tool === "eraser") setTool("pen"); }} data-testid={`anno-color-${c.slice(1)}`}
              className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
              style={{ background: c, borderColor: color === c && tool !== "eraser" ? "#fff" : "transparent" }} />
          ))}
          <span className="w-px h-5 bg-white/15 mx-0.5" />
          {SIZES.map((s) => (
            <button key={s} onClick={() => setSize(s)} data-testid={`anno-size-${s}`}
              className={`w-7 h-7 rounded-md inline-flex items-center justify-center ${size === s ? "bg-white/15" : "hover:bg-white/10"}`}>
              <span className="rounded-full bg-white" style={{ width: s + 2, height: s + 2 }} />
            </button>
          ))}
          <span className="w-px h-5 bg-white/15 mx-0.5" />
          <button className={tb(false)} onClick={undo} data-testid="anno-undo" title="Undo"><Undo2 size={14} /></button>
          <button className={tb(false)} onClick={clearAll} data-testid="anno-clear" title="Clear all"><Trash2 size={14} /></button>
          <button
            onClick={() => setActive(false)}
            data-testid="anno-exit"
            title="Done annotating"
            className="ml-0.5 w-8 h-8 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/35 inline-flex items-center justify-center"
          ><X size={14} /></button>
        </div>
      )}
    </>
  );
}
